// src/pages/subdomain-edit.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getSubdomain, saveSubdomain, getAllServices, getAllSocieties } from '../services/api.service';
import Helper from '../services/helper.service';
import { FaChevronLeft } from 'react-icons/fa';
import { useConfirm } from '../components/confirm-dialog';

const defaultData = {
  subdomain: '',
  fullName: '',
  logoUrl: 'https://api.leclubby.com/uploads/',
  pouvoirPath: 'https://api.leclubby.com/uploads/Pouvoir_Fiska_France.pdf',
  price: 35,
  apiEndpoint: '',
  services: [],
  societyId: ''
};

const SubdomainEditorPage = () => {
  const { subdomain } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(subdomain && subdomain !== 'new');

  const [formData, setFormData] = useState(defaultData);
  const [allServices, setAllServices] = useState([]);
  const [societies, setSocieties] = useState([]);
  const [activeTab, setActiveTab] = useState('general');
  const [saved, setSaved] = useState(false);
  const [errors, setErrors] = useState({});

  const confirm = useConfirm();

  useEffect(() => {
    if (isEdit && subdomain !== "new") {
      getSubdomain(subdomain)
        .then((data) => {
          // Ensure we have a societyId field even if backend hasn't added it yet
          setFormData(prev => ({
            ...defaultData,
            ...(data || {}),
            societyId: data?.societyId || '' // keep as empty string if not set yet
          }));
        })
        .catch(() => {
          Helper.toast("Impossible de charger les données", "error");
        });
    }
  }, [subdomain, isEdit]);

  useEffect(() => {
    getAllServices()
      .then(setAllServices)
      .catch(() => Helper.toast("Erreur chargement des services", "error"));

    // ⬇️ fetch societies instead of banks
    getAllSocieties()
      .then(setSocieties)
      .catch(() => Helper.toast("Erreur chargement des sociétés", "error"));
  }, []);

  const handleChange = ({ target: { name, value } }) => {
    setFormData((prev) => ({
      ...prev,
      [name]: name === 'price' ? (value === '' ? '' : parseFloat(value)) : value,
    }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const toggleService = (serviceId) => {
    setFormData(prev => {
      const exists = prev.services.find(s => s.serviceId === serviceId);

      return {
        ...prev,
        services: exists
          ? prev.services.filter(s => s.serviceId !== serviceId)
          : [...prev.services, {
            serviceId,
            customPrice: allServices.find(s => s._id === serviceId)?.price ?? 0,
            endpointId: ''
          }]
      };
    });
  };

  const updateServicePrice = (serviceId, price) => {
    setFormData(prev => ({
      ...prev,
      services: prev.services.map(s =>
        s.serviceId === serviceId ? { ...s, customPrice: parseFloat(price) || 0 } : s
      ),
    }));
  };

  const updateServiceEndpointId = (serviceId, endpointId) => {
    setFormData(prev => ({
      ...prev,
      services: prev.services.map(s =>
        s.serviceId === serviceId ? { ...s, endpointId } : s
      ),
    }));
  };

  const validate = () => {
    const e = {};
    let firstTabToOpen = null;

    if (!isEdit && !formData.subdomain?.trim()) {
      e.subdomain = 'Le sous-domaine est requis';
      firstTabToOpen ||= 'general';
    }
    if (!formData.fullName?.trim()) {
      e.fullName = 'Le nom complet est requis';
      firstTabToOpen ||= 'general';
    }
    // If backend requires societyId, enforce it here
    if (!formData.societyId) {
      e.societyId = 'Veuillez sélectionner une société';
      firstTabToOpen ||= 'society';
    }

    if (formData.apiEndpoint && !/^https?:\/\/.+/i.test(formData.apiEndpoint.trim())) {
      e.apiEndpoint = 'Veuillez saisir une URL valide commençant par http:// ou https://';
      firstTabToOpen ||= 'general';
    }

    setErrors(e);
    if (firstTabToOpen) setActiveTab(firstTabToOpen);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      Helper.toast("Merci de corriger les champs manquants.", "error");
      return;
    }

    const payload = {
      ...formData,
      societyId: formData.societyId || undefined,
      price: Number(formData.price) || 0,
      apiEndpoint: formData.apiEndpoint?.trim() || '',
      services: (formData.services || []).map(s => ({
        serviceId: s.serviceId,
        customPrice: Number(s.customPrice) || 0,
        endpointId: s.endpointId?.trim() || ''
      }))
    };

    try {
      // Try normal save
      await saveSubdomain(payload);

      setSaved(true);
      setTimeout(() => {
        setSaved(false);
        navigate('/subdomains');
      }, 1000);
    } catch (err) {
      // If creating a new subdomain and it already exists, ask for overwrite
      if (!isEdit && (err?.code === 'SUBDOMAIN_EXISTS' || err?.status === 409)) {
        const shouldOverride = await confirm({
          title: "Sous-domaine existant",
          message: `${err.message}\n\nVoulez-vous écraser l'existant ?`,
          confirmText: "Écraser",
          cancelText: "Annuler",
          tone: "warning",
        });

        if (!shouldOverride) {
          Helper.toast("Action annulée, aucun changement effectué.", "info");
          return;
        }

        // Overwrite the existing record
        try {
          await saveSubdomain({ ...payload, overwrite: true });

          setSaved(true);
          setTimeout(() => {
            setSaved(false);
            navigate('/subdomains');
          }, 1000);
        } catch (err2) {
          Helper.toast(err2?.message || "Erreur lors de l'écrasement du sous-domaine", "error");
        }

        return;
      }

      // Any other error
      Helper.toast(err?.message || "Erreur lors de l'enregistrement du sous-domaine", "error");
    }
  };


  // Find selected society and derive a bank display name
  const selectedSociety = useMemo(
    () => societies.find(s => s._id === formData.societyId),
    [societies, formData.societyId]
  );

  // Prefer populated bankId.name if backend populates, else society.bank.name (invoice bank data)
  const selectedBankName =
    (selectedSociety?.bankId && typeof selectedSociety.bankId === 'object'
      ? (selectedSociety.bankId.name?.fr || selectedSociety.bankId.name)
      : '') || selectedSociety?.bank?.name || '';

  return (
    <div className="max-w-5xl mx-auto p-6 bg-white rounded-lg shadow-lg">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <button onClick={() => navigate('/subdomains')} className="text-gray-600 flex items-center hover:text-gray-900">
          <FaChevronLeft />&nbsp;Retour
        </button>
        <button onClick={handleSubmit} className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded shadow">
          Enregistrer
        </button>
      </div>

      {/* Tabs */}
      <div className="flex border-b mb-6">
        {['general', 'services', 'society'].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 -mb-px ${activeTab === tab ? 'border-b-2 border-blue-500 font-semibold' : 'text-gray-500'}`}
          >
            {tab === 'general' && 'Général'}
            {tab === 'services' && 'Prestations'}
            {tab === 'society' && 'Société'}
          </button>
        ))}
      </div>

      {/* General Tab */}
      {activeTab === 'general' && (
        <div className="space-y-4">
          {!isEdit && (
            <div>
              <label className="block font-semibold mb-1">Sous-domaine</label>
              <input
                type="text"
                name="subdomain"
                value={formData.subdomain}
                onChange={handleChange}
                className="w-full border px-3 py-2 rounded shadow-sm"
                required
              />
              {errors.subdomain && <p className="text-sm text-red-600 mt-1">{errors.subdomain}</p>}
            </div>
          )}
          <div>
            <label className="block font-semibold mb-1">Nom complet</label>
            <input
              type="text"
              name="fullName"
              value={formData.fullName}
              onChange={handleChange}
              className="w-full border px-3 py-2 rounded shadow-sm"
              required
            />
            {errors.fullName && <p className="text-sm text-red-600 mt-1">{errors.fullName}</p>}
          </div>
          <div>
            <label className="block font-semibold mb-1">Logo (URL)</label>
            <input
              type="text"
              name="logoUrl"
              value={formData.logoUrl}
              onChange={handleChange}
              className="w-full border px-3 py-2 rounded shadow-sm"
            />
          </div>
          <div>
            <label className="block font-semibold mb-1">API endpoint</label>
            <input
              type="text"
              name="apiEndpoint"
              value={formData.apiEndpoint || ''}
              onChange={handleChange}
              placeholder="https://api.example.com/public/services/prices-by-user"
              className="w-full border px-3 py-2 rounded shadow-sm"
            />
            {errors.apiEndpoint && (
              <p className="text-sm text-red-600 mt-1">{errors.apiEndpoint}</p>
            )}
            <p className="text-xs text-gray-500 mt-1">
              Endpoint appelé pour récupérer les prix selon l’utilisateur et les prestations.
            </p>
          </div>
        </div>
      )}

      {/* Services Tab */}
      {activeTab === 'services' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {allServices.map((service) => {
            const selected = formData.services.find(s => s.serviceId === service._id);
            return (
              <div
                key={service._id}
                className={`p-4 rounded-lg border shadow-sm transition-all duration-200 hover:shadow-md ${selected ? 'bg-blue-50 border-blue-500' : 'bg-white border-gray-200'}`}
              >
                <div className='flex flex-col justify-between h-full'>
                  <div>
                    <div className="flex justify-between items-start mb-2">
                      <h3 className="font-semibold text-gray-800">
                        {service.title?.fr || 'Sans titre'}
                      </h3>

                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={!!selected}
                          onChange={() => toggleService(service._id)}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-500 rounded-full peer peer-checked:bg-blue-500 transition-all duration-200" />
                        <div className="absolute left-1 top-1 w-4 h-4 bg-white rounded-full transition-all duration-200 peer-checked:translate-x-5" />
                      </label>
                    </div>

                    <div
                      className="text-sm text-gray-600 mb-3 max-h-24 overflow-y-auto"
                      dangerouslySetInnerHTML={{ __html: service.shortDescription?.fr }}
                    />
                  </div>

                  {selected && (
                    <div className="space-y-3 mt-3">
                      <div className="flex items-center justify-between gap-4">
                        <label className="text-sm text-gray-700">Prix personnalisé (€)</label>
                        <input
                          type="number"
                          step="1"
                          className="w-24 border border-gray-300 rounded px-2 py-1 text-right"
                          value={selected.customPrice}
                          onChange={(e) => updateServicePrice(service._id, e.target.value)}
                        />
                      </div>

                      {formData.apiEndpoint?.trim() && (
                        <div>
                          <label className="block text-sm text-gray-700 mb-1">
                            Endpoint ID
                          </label>
                          <input
                            type="text"
                            className="w-full border border-gray-300 rounded px-2 py-1"
                            value={selected.endpointId || ''}
                            onChange={(e) => updateServiceEndpointId(service._id, e.target.value)}
                            placeholder="ID utilisé par l’API externe"
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            Utilisé pour récupérer le prix depuis l’endpoint. Le prix personnalisé reste utilisé en fallback.
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Society Tab (replaces Bank) */}
      {activeTab === 'society' && (
        <div className="space-y-4">
          <label className="block font-semibold mb-1">Société associée</label>
          <div className="relative w-full sm:w-96">
            <select
              value={formData.societyId || ''}
              onChange={(e) => setFormData(prev => ({ ...prev, societyId: e.target.value }))}
              className="appearance-none bg-white w-full border rounded px-3 py-2 pr-10 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Aucune société sélectionnée</option>
              {societies.map((soc) => (
                <option key={soc._id} value={soc._id}>
                  {soc.name}
                </option>
              ))}
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-gray-500">
              <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </div>
            {errors.societyId && <p className="text-sm text-red-600 mt-1">{errors.societyId}</p>}
          </div>

          {/* Show bank name for the selected society */}
          {formData.societyId && (
            <p className="text-sm text-gray-600">
              Banque : <span className="font-medium text-gray-800">{selectedBankName || '—'}</span>
            </p>
          )}
        </div>
      )}

      {saved && <p className="text-green-600 mt-4">Enregistré avec succès !</p>}
    </div>
  );
};

export default SubdomainEditorPage;